import { Controller } from "stimulus";

export default class extends Controller {
    connect() {
        this.formId = this.element.querySelector('form[id^="gform_"]')
            ?.id.replace('gform_', '');

        if (!this.formId) return;

        this.priceMap = this.parsePrices();
        this.stripModelPrefixes();
        this.modelFieldId = this.findModelFieldId();
        this.imageMap = this.parseImageMap();
        this.labelSpans = this.setupLabelSpans();
        this.buildVehiclePreview();
        this.priceSectionTitleEl = this.element.querySelector('.vehicle-price .gsection_title');
        this.bindEvents();
        this.triggerConditionalLogic();
        this.update();
    }

    parsePrices() {
        const priceMap = {};

        this.element.querySelectorAll('input[type="radio"], input[type="hidden"]').forEach(input => {
            if (input.closest('.presale-dealership')) return;
            const match = input.value.match(/^(.+)::(\d+(?:\.\d+)?)$/);
            if (!match) return;
            const id = input.name.replace('input_', '');
            if (!priceMap[id]) priceMap[id] = {};
            priceMap[id][match[1].trim()] = parseFloat(match[2]);
        });

        this.element.querySelectorAll('select option').forEach(option => {
            if (option.closest('.presale-dealership')) return;
            const match = option.value.match(/^(.+)::(\d+(?:\.\d+)?)$/);
            if (!match) return;
            const id = option.closest('select').name.replace('input_', '');
            if (!priceMap[id]) priceMap[id] = {};
            priceMap[id][match[1].trim()] = parseFloat(match[2]);
        });

        return priceMap;
    }

    stripModelPrefixes() {
        this.element.querySelectorAll('.gfield_label').forEach(label => {
            label.childNodes.forEach(node => {
                if (node.nodeType === Node.TEXT_NODE) {
                    node.textContent = node.textContent.replace(/^\[model:[^\]]+\]\s*/, '');
                }
            });
        });
    }

    getColourVal() {
        for (const container of this.element.querySelectorAll('.exterior-color')) {
            if (container.closest('.gfield')?.style.display === 'none') continue;
            const sel = container.querySelector('select[name^="input_"]');
            if (sel) return sel.value.replace(/::[\d.]+$/, '') || null;
            const checked = container.querySelector('input[type="radio"]:checked');
            if (checked) return checked.value.replace(/::[\d.]+$/, '') || null;
        }
        return null;
    }

    parseImageMap() {
        try {
            return JSON.parse(this.element.dataset.images || '[]');
        } catch (e) {
            return [];
        }
    }

    findImage(model, colour) {
        if (!model || !colour) return null;
        const entry = this.imageMap.find(item =>
            item.model.toLowerCase() === model.toLowerCase() &&
            item.colour.toLowerCase() === colour.toLowerCase()
        );
        return entry ? entry.image : null;
    }

    findModelFieldId() {
        if (this.data.has('modelField')) return this.data.get('modelField');

        const sel = [...this.element.querySelectorAll('select[name^="input_"]')]
            .find(s => !s.closest('.presale-dealership'));
        return sel ? sel.name.replace('input_', '') : null;
    }

    setupLabelSpans() {
        const spans = {};
        Object.keys(this.priceMap).forEach(id => {
            if (id === this.modelFieldId) return;
            const fieldEl = this.element.querySelector(`#field_${this.formId}_${id}`);
            if (!fieldEl) return;
            const labelEl = fieldEl.querySelector('.gfield_label');
            if (!labelEl) return;
            const span = document.createElement('span');
            span.className = 'gfield_label__choice-value';
            labelEl.appendChild(span);
            fieldEl.classList.add('gfield--priced-option');
            spans[id] = span;
        });
        return spans;
    }

    buildVehiclePreview() {
        this.vehiclePreviewEl = this.element.querySelector('.vehicle-preview');
        if (!this.vehiclePreviewEl) return;

        this.vehiclePreviewEl.innerHTML = `
            <img class="vehicle-preview__image" src="" alt="" aria-hidden="true">
        `;

        this.imageEl = this.vehiclePreviewEl.querySelector('.vehicle-preview__image');
    }

    fmt(n) {
        return '$' + n.toLocaleString('en-NZ');
    }

    getVal(id) {
        const strip = v => v ? v.replace(/::[\d.]+$/, '') : null;
        const sel = this.element.querySelector(`select[name="input_${id}"]`);
        if (sel) return strip(sel.value);
        const checked = this.element.querySelector(`[name="input_${id}"]:checked`);
        return checked ? strip(checked.value) : null;
    }

    getChoiceText(id) {
        const sel = this.element.querySelector(`select[name="input_${id}"]`);
        if (sel && sel.selectedIndex >= 0) return sel.options[sel.selectedIndex].text;
        const checked = this.element.querySelector(`[name="input_${id}"]:checked`);
        if (!checked) return null;
        const lbl = this.element.querySelector(`label[for="${checked.id}"]`);
        return lbl ? lbl.textContent.trim() : checked.value;
    }

    update() {
        const modelVal = this.getVal(this.modelFieldId);
        const colourVal = this.getColourVal();

        if (this.vehiclePreviewEl) {
            const imageUrl = this.findImage(modelVal, colourVal);
            if (imageUrl) {
                this.imageEl.src = imageUrl;
                this.imageEl.alt = [modelVal, colourVal].filter(Boolean).join(' — ');
                this.vehiclePreviewEl.classList.add('has-image');
            } else {
                this.imageEl.src = '';
                this.vehiclePreviewEl.classList.remove('has-image');
            }
        }

        const basePrice = modelVal && this.priceMap[this.modelFieldId]?.[modelVal] != null
            ? this.priceMap[this.modelFieldId][modelVal]
            : null;

        if (basePrice === null) {
            if (this.priceSectionTitleEl) this.priceSectionTitleEl.textContent = '';
            return;
        }

        let optionsTotal = 0;
        Object.keys(this.priceMap).forEach(id => {
            if (id === this.modelFieldId) return;
            const val = this.getVal(id);
            const price = val != null && this.priceMap[id]?.[val] != null ? this.priceMap[id][val] : 0;
            optionsTotal += price;
            if (this.labelSpans[id]) {
                const text = this.getChoiceText(id) || '';
                this.labelSpans[id].textContent = text
                    ? text + (price > 0 ? ` (+${this.fmt(price)})` : '')
                    : '';
            }
        });

        const total = basePrice + optionsTotal;
        const includesText = optionsTotal > 0 ? 'Includes options' : '';

        if (this.priceSectionTitleEl) {
            this.priceSectionTitleEl.innerHTML = `Price: ${this.fmt(total)}${includesText ? ` <span class="price-includes-options">${includesText}</span>` : ''}`;
        }
    }

    triggerConditionalLogic() {
        const modelSelect = this.element.querySelector(`select[name="input_${this.modelFieldId}"]`);
        if (modelSelect) {
            modelSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    bindEvents() {
        const deferredUpdate = () => setTimeout(() => this.update(), 0);

        Object.keys(this.priceMap).forEach(id => {
            this.element.querySelectorAll(`[name="input_${id}"]`)
                .forEach(input => input.addEventListener('change', deferredUpdate));
        });

        this.element.querySelectorAll('.exterior-color input[type="radio"], .exterior-color select')
            .forEach(input => input.addEventListener('change', deferredUpdate));
    }
}
